import moment from 'moment';
import { NOTIFICATION_STATUS_CODE } from '../../../enums/notification-status-code.enum';
import { latestTaskForCode, taskToStatusRecord } from '../notification-collection.converters';
import {
    NotificationState,
    NotificationStatusAdded,
    NotificationStatusDiscrepancy,
} from '../notification-collection.state';
import { durationRecordForArrival, isSameStatusWindow, upsertStatusRecords } from '../notification-status.rules';
import {
    backendStatusCodeForEntry,
    effectiveStatusForEntry,
    isCancelledStatusCode,
    isConfirmedOrCancellationConfirmedCode,
    isInProgressOrToBeSignedCode,
} from '../selectors/notification.selector';

/** What reconciling a freshly loaded collection produced. */
export type LoadReconciliation = Readonly<{
    entries: NotificationState[];
    /**
     * The earliest travel the reconciliation saw, as an ISO timestamp, or `undefined` when it saw
     * none. The timesheet's start of work is pulled back to it — see
     * `TimesheetEffects.pullBackStartOfWork$`, which is where that write belongs.
     */
    earliestTravel: string | undefined;
    /** The statuses appended, so the load can log them the way a dispatched status write is logged. */
    addedStatuses: NotificationStatusAdded[];
    /** The recorded statuses the tasks corrected, so the load can warn about them. */
    statusDiscrepancies: NotificationStatusDiscrepancy[];
}>;

/**
 * The entry with its DURA derived from its ETA, when the backend sent no DURA task to take it from.
 * Not a discrepancy when it changes: a corrected ETA has already been reported as one, and the DURA
 * merely follows it. A newly derived DURA is reported as added, like any other appended status.
 *
 * @param entry the reconciled entry
 * @param addedStatuses collects the statuses appended to the entries
 */
function withDurationDerivedFromArrival(
    entry: NotificationState,
    addedStatuses: NotificationStatusAdded[],
): NotificationState {
    const arrival = entry.status.find((status) => status.statusCode === NOTIFICATION_STATUS_CODE.ETA);
    if (!arrival || latestTaskForCode(entry.tasks, NOTIFICATION_STATUS_CODE.DURA)) {
        return entry;
    }
    const duration = durationRecordForArrival(entry, arrival.endTime);
    const recorded = entry.status.find((status) => status.statusCode === NOTIFICATION_STATUS_CODE.DURA);
    if (recorded && isSameStatusWindow(recorded, duration)) {
        return entry;
    }
    if (!recorded) {
        addedStatuses.push({ id: entry.notificationId, status: duration });
    }
    return { ...entry, status: upsertStatusRecords(entry.status, [duration]) };
}

/**
 * Reconciles what the device recorded about each notification with what the backend just sent:
 * every status the `taskSet` reports is written onto the entry (highest `TaskNumber` per code wins) —
 * appended where the device has none for that code, and replacing the device's window where it
 * differs, since the backend is the source of truth and the device only predicted it — and a
 * cancellation is recorded (AVRF while the work is under way, AVR1 otherwise) when the backend
 * cancelled a notification the device still thinks is open.
 *
 * Where the backend sent an ETA but no DURA task — it can be configured not to create one — the
 * device's DURA is (re-)derived from the reconciled ETA instead, the same way an ETA submit derives
 * it, so the window shown for an announced arrival never depends on that configuration.
 *
 * An entry whose confirmation is already through is left alone — nothing the backend sends about it
 * can change what was signed off.
 *
 * The earliest travel it comes across travels back out rather than being acted on: pulling the
 * timesheet's start of work back to it is the timesheet slice's business, and a reducer has no
 * other slice to write to. The same goes for the appended statuses, which the load logs.
 *
 * Pure, so that the load reducer can own it: the entries carry everything it reads, and `isAppStart`
 * comes in with the load rather than being looked up.
 *
 * @param entries the freshly loaded entries, with the recorded status already carried over
 * @param isAppStart whether this load is the one performed during app start
 */
export function reconcileLoadedStatuses(entries: NotificationState[], isAppStart: boolean): LoadReconciliation {
    let earliestTravel: moment.Moment | undefined;
    const addedStatuses: NotificationStatusAdded[] = [];
    const statusDiscrepancies: NotificationStatusDiscrepancy[] = [];

    const reconciled = entries.map((entry) => {
        if (isConfirmedOrCancellationConfirmedCode(effectiveStatusForEntry(entry).statusCode)) {
            return entry;
        }
        const backendStatusCode = backendStatusCodeForEntry(entry);
        const taskCodes = new Set(entry.tasks.map((task) => task.statusCode));
        let reconciledEntry = entry;

        taskCodes.forEach((taskCode) => {
            const task = latestTaskForCode(reconciledEntry.tasks, taskCode);
            if (!task) {
                return;
            }
            const status = taskToStatusRecord(task);
            const recorded = reconciledEntry.status.find((existing) => existing.statusCode === status.statusCode);
            if (!recorded) {
                addedStatuses.push({ id: reconciledEntry.notificationId, status });
                reconciledEntry = { ...reconciledEntry, status: [...reconciledEntry.status, status] };
            } else if (!isSameStatusWindow(recorded, status)) {
                statusDiscrepancies.push({ id: reconciledEntry.notificationId, recorded, task: status });
                reconciledEntry = { ...reconciledEntry, status: upsertStatusRecords(reconciledEntry.status, [status]) };
            }

            if (!isAppStart && backendStatusCode === effectiveStatusForEntry(reconciledEntry).statusCode) {
                return;
            }

            if (
                taskCode !== backendStatusCode &&
                isCancelledStatusCode(backendStatusCode) &&
                !reconciledEntry.cancelled
            ) {
                reconciledEntry = {
                    ...reconciledEntry,
                    cancelled: isInProgressOrToBeSignedCode(status.statusCode)
                        ? NOTIFICATION_STATUS_CODE.AVRF
                        : NOTIFICATION_STATUS_CODE.AVR1,
                };
            }

            const startTime = moment(status.startTime);
            if (
                status.statusCode === NOTIFICATION_STATUS_CODE.TRAV &&
                (earliestTravel === undefined || startTime.isBefore(earliestTravel))
            ) {
                earliestTravel = startTime;
            }
        });

        return withDurationDerivedFromArrival(reconciledEntry, addedStatuses);
    });

    return { entries: reconciled, earliestTravel: earliestTravel?.toJSON(), addedStatuses, statusDiscrepancies };
}
